
const USER_KEY = '$user$'
const REMEMBER_KEY = '$remember_username$'
const REMEMBER_CREDENTIALS_KEY = '$remember_credentials$'
const LAST_ACTIVITY_KEY = '$last_auth_activity$'

export const AUTH_IDLE_TIMEOUT_MS = 6 * 60 * 60 * 1000

type UserPermission = {
  resource?: string
  action?: string
}

type LoginUser = {
  id?: string
  username?: string
  nickname?: string
  email?: string
  phone?: string
  avatar?: string
  lang?: string
  is_admin?: boolean
  tenant_id?: string
  role?: string
  role_level?: number
  permissions?: UserPermission[]
}

type LoginState = {
  token?: string
  user?: LoginUser
  mustChangePassword?: boolean
}

type RememberCredentials = {
  username: string
  password: string
}

const ROLE_LEVEL: Record<string, number> = {
  superadmin: 4,
  admin: 3,
  tenant_admin: 3,
  member: 2,
  operator: 2,
  viewer: 1,
  readonly: 1,
}

const normalize = (value?: string) => (value || '').trim().toLowerCase()

const normalizeRole = (role?: string): string => {
  const normalized = normalize(role)
  if (normalized === 'tenant_admin') return 'admin'
  if (normalized === 'operator') return 'member'
  if (normalized === 'readonly') return 'viewer'
  return normalized
}

const normalizeResource = (resource?: string): string => normalize(resource)
const normalizeAction = (action?: string): string => normalize(action)

const getAuthState = (): LoginState | null => getUser()

export const markAuthActivity = (now: number = Date.now()) => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
}

export const getLastAuthActivity = (): number => {
  const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0)
  return Number.isFinite(value) && value > 0 ? value : 0
}

export const hasAuthIdleExpired = (now: number = Date.now()): boolean => {
  const lastActivity = getLastAuthActivity()
  return lastActivity > 0 && now - lastActivity >= AUTH_IDLE_TIMEOUT_MS
}

export const subscribeAuth = (listener: () => void): (() => void) => {
  window.addEventListener('authchange', listener)
  window.addEventListener('storage', listener)
  return () => { window.removeEventListener('authchange', listener); window.removeEventListener('storage', listener) }
}
export const getAuthSnapshot = () => localStorage.getItem(USER_KEY) || ''
export const setUser = (user: any, trackActivity = true) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  window.dispatchEvent(new Event('authchange'))
  if (trackActivity) markAuthActivity()
}
export const getUser = (): LoginState | null => {
  const userJson = localStorage.getItem(USER_KEY)
  if (userJson) {
    try {
      return JSON.parse(userJson)
    } catch (error) {
      console.error(error)
    }
  }
  return null
}
export const clearUser = () => {
  localStorage.removeItem(USER_KEY)
  window.dispatchEvent(new Event('authchange'))
  localStorage.removeItem(LAST_ACTIVITY_KEY)
}
export const getToken = () => getAuthState()?.token || ''

export const isAdmin = () => getAuthState()?.user?.is_admin === true

export const getTenantId = () => getAuthState()?.user?.tenant_id || ''

export const getRole = (): string => normalizeRole(getAuthState()?.user?.role)

export const getRoleLevel = (): number => {
  const user = getAuthState()?.user
  if (!user) return 0
  if (user.is_admin) return ROLE_LEVEL.superadmin
  if (typeof user.role_level === 'number' && user.role_level > 0) {
    return user.role_level
  }
  const role = normalizeRole(user.role)
  return ROLE_LEVEL[role] || 0
}

export const hasPermission = (resource: string, action: string): boolean => {
  const permissions = getAuthState()?.user?.permissions || []
  const targetResource = normalizeResource(resource)
  const targetAction = normalizeAction(action)

  if (!targetResource || !targetAction) return false

  return permissions.some((permission) => {
    const permissionResource = normalizeResource(permission.resource)
    const permissionAction = normalizeAction(permission.action)

    if (targetAction === 'read_secret' && permissionAction === '*' && permissionResource !== '*') return false

    const resourceMatched = permissionResource === '*' || permissionResource === targetResource
    const actionMatched = permissionAction === '*' || permissionAction === targetAction

    return resourceMatched && actionMatched
  })
}

// Role names and display levels carry no authority. The backend checks the
// recipient and delegated scopes for every account mutation.
// Preserve the legacy call signature; role labels do not determine authority.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const canManageTargetRole = (_targetRole?: string): boolean => hasPermission('user', 'update')

export const canAccessSystemUserPage = (): boolean => hasPermission('user', 'read')

export const getDisplayName = (): string => {
  const user = getAuthState()?.user
  if (!user) return ''
  return user.nickname || user.username || ''
}

export const getUserId = (): string => getAuthState()?.user?.id || ''

export const getMustChangePassword = (): boolean => getAuthState()?.mustChangePassword === true

export const setRememberUsername = (username: string) => localStorage.setItem(REMEMBER_KEY, username)
export const getRememberUsername = () => localStorage.getItem(REMEMBER_KEY) || ''
export const clearRememberUsername = () => localStorage.removeItem(REMEMBER_KEY)

export const setRememberCredentials = (username: string, password: string) => {
  const credentials: RememberCredentials = {
    username: username || '',
    password: password || '',
  }
  localStorage.setItem(REMEMBER_CREDENTIALS_KEY, JSON.stringify(credentials))
  setRememberUsername(credentials.username)
}

export const getRememberCredentials = (): RememberCredentials => {
  const credentialsJson = localStorage.getItem(REMEMBER_CREDENTIALS_KEY)
  if (credentialsJson) {
    try {
      const credentials = JSON.parse(credentialsJson) as Partial<RememberCredentials>
      return {
        username: credentials.username || '',
        password: credentials.password || '',
      }
    } catch (error) {
      console.error(error)
    }
  }
  return {
    username: getRememberUsername(),
    password: '',
  }
}

export const clearRememberCredentials = () => {
  localStorage.removeItem(REMEMBER_CREDENTIALS_KEY)
  clearRememberUsername()
}
